import { useMemo, useState } from 'react'

import { API } from '@/api'
import { DEBOUNCE_DELAYS, PAYMENT_CURRENCY } from '@/consts/billing'
import type { Currency } from '@/types/billing'
import { formatPaymentAmount } from '@/utils/billing'
import { debounce } from '@/utils/debounce'

export function usePaymentCalculation(currency: Currency) {
  const [paymentAmount, setPaymentAmount] = useState<number>(0)
  const [isCalculating, setIsCalculating] = useState(true)

  const debouncedFetchPaymentAmount = useMemo(() => {
    const fetchAmount = async (creditCent: number) => {
      try {
        setIsCalculating(true)
        const payCurrency = PAYMENT_CURRENCY[currency]
        const res = await API.getExchangeRate({
          credit_cent: creditCent,
          pay_currency: payCurrency,
        })
        if (res.code === 0) {
          setPaymentAmount(res.amount)
        }
      } catch (error) {
        console.error('Failed to fetch payment amount:', error)
      } finally {
        setIsCalculating(false)
      }
    }

    return debounce(fetchAmount, DEBOUNCE_DELAYS.PAYMENT_AMOUNT)
  }, [currency])

  const feeInCurrency = useMemo(() => {
    return formatPaymentAmount(paymentAmount, currency, isCalculating)
  }, [paymentAmount, currency, isCalculating])

  return {
    paymentAmount,
    setPaymentAmount,
    isCalculating,
    setIsCalculating,
    feeInCurrency,
    debouncedFetchPaymentAmount,
  }
}
